import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ModalAddAttachmentComponent } from '../modal-add-attachment/modal-add-attachment.component';
import { SurveyService } from '../../services/surveys.service';
import { AttachmentPreviewService } from '../../services/attachment-preview.service';
import { ModalDocumentViewerComponent } from '../modal-document-viewer/modal-document-viewer.component';
import { sortStatementAttachments } from '../../components/surveys/statement-documents/statement-documents.component';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../services/general/toast.service';

@Component({
    selector: 'app-modal-show-evidences',
    templateUrl: './modal-show-evidences.component.html',
    styleUrls: ['./modal-show-evidences.component.scss'],
    standalone: false
})
export class ModalShowEvidencesComponent implements OnInit {
  userFullName: any;
  displayedColumns = ['name', 'type', 'loaded_at', 'actions'];
  attachments = [];
  editable = false;
  manageDisplay = false;
  displayDirty = false;
  savingDisplay = false;

  constructor(
    public dialogRef: MatDialogRef<ModalShowEvidencesComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      userFullName?: string,
      attachments: any[],
      editable?: boolean,
      surveyId?: number,
      questionId?: number,
      getSurveyId?: () => number,
      getQuestionId?: () => number,
      manageDisplay?: boolean
    },
    private dialog: MatDialog,
    private surveyService: SurveyService,
    public preview: AttachmentPreviewService,
    private translateService: TranslateService,
    private toast: ToastService
  ) {
    this.userFullName = data.userFullName;
    this.editable = data.editable || false;
    this.manageDisplay = !!data.manageDisplay && this.editable;
    this.attachments = this.manageDisplay ? sortStatementAttachments(data.attachments || []) : data.attachments;
  }

  get canPersistDisplay(): boolean {
    return !!this.surveyId && !!this.questionId && this.attachments.every((attachment) => Number.isInteger(attachment?.id));
  }

  get primaryCount(): number {
    return this.attachments.filter((attachment) => attachment.is_primary).length;
  }

  dropAttachment(event: CdkDragDrop<any[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    moveItemInArray(this.attachments, event.previousIndex, event.currentIndex);
    this.attachments = [...this.attachments];
    this.markDisplayDirty();
  }

  togglePrimary(attachment: any): void {
    attachment.is_primary = !attachment.is_primary;
    this.markDisplayDirty();
  }

  toggleReadOnly(attachment: any): void {
    attachment.is_read_only = !attachment.is_read_only;
    this.markDisplayDirty();
  }

  markDisplayDirty(): void {
    this.attachments.forEach((attachment, index) => (attachment.position = index));
    this.displayDirty = true;
    this.syncSourceAttachments();
  }

  saveDisplay(): void {
    if (!this.canPersistDisplay || this.savingDisplay) {
      return;
    }
    this.savingDisplay = true;
    const payload = this.attachments.map((attachment, index) => ({
      id: attachment.id,
      type: attachment.type,
      position: index,
      is_primary: !!attachment.is_primary,
      title: attachment.title,
      reference: attachment.reference || null,
      page: attachment.page ? Number(attachment.page) : null,
      ...(attachment.type === 'FILE' ? { is_read_only: !!attachment.is_read_only } : {}),
    }));
    this.surveyService.updateQuestionAttachmentsDisplay(this.surveyId!, this.questionId!, payload, (saved) => {
      this.savingDisplay = false;
      this.displayDirty = false;
      this.attachments = sortStatementAttachments(saved);
      this.syncSourceAttachments();
      this.toast.success(
        this.translateService.instant('generic.success'),
        this.translateService.instant('modals.manage-attachments.saved')
      );
    }, () => {
      this.savingDisplay = false;
    });
  }

  openInViewer(attachment: any): void {
    if (attachment.type !== 'FILE') {
      if (attachment.link) {
        window.open(attachment.link, '_blank', 'noopener');
      }
      return;
    }
    if (!this.surveyId || !this.questionId || !Number.isInteger(attachment.id)) {
      return;
    }
    const documents = this.attachments.filter((item) => item.type === 'FILE' && Number.isInteger(item.id));
    this.dialog.open(ModalDocumentViewerComponent, {
      data: { surveyId: this.surveyId, questionId: this.questionId, attachments: documents, index: Math.max(0, documents.indexOf(attachment)) },
      panelClass: 'document-viewer-dialog',
      width: '96vw',
      maxWidth: '96vw',
      height: '94vh',
      autoFocus: false,
    });
  }

  private syncSourceAttachments(): void {
    if (this.manageDisplay && Array.isArray(this.data.attachments)) {
      this.data.attachments.splice(0, this.data.attachments.length, ...this.attachments);
    }
  }

  get surveyId(): number | undefined {
    return this.data.getSurveyId ? this.data.getSurveyId() : this.data.surveyId;
  }

  get questionId(): number | undefined {
    return this.data.getQuestionId ? this.data.getQuestionId() : this.data.questionId;
  }

  ngOnInit(): void {
    if (this.editable) {
      this.displayedColumns = ['name', 'type', 'loaded_at', 'actions'];
    } else {
      this.displayedColumns = ['name', 'type', 'loaded_at', 'actions'];
    }
    // Note: 'actions' will contain both View and Delete (if editable)
  }

  public closeDialogue(event: any = null) {
    this.dialogRef.close(event);
  }

  public updateAttachments(newAttachments: any[]) {
    this.attachments = newAttachments;
    // No need for [...this.attachments] if we replace it,
    // but just in case for mat-table reactivity:
    this.attachments = [...this.attachments];
  }

  deleteAttachment(index: number) {
    if (index >= 0 && index < this.attachments.length) {
      const attachment = this.attachments[index];

      // If we have an ID and surveyId, it's a persisted attachment on a non-draft survey
      if (attachment.id && this.surveyId) {
        if (this.questionId) {
          this.surveyService.deleteQuestionAttachment(this.surveyId, this.questionId, attachment.id, () => {
             this.finalizeDeletion(index);
          });
        } else {
          this.surveyService.deleteSurveyAttachment(this.surveyId, attachment.id, () => {
             this.finalizeDeletion(index);
          });
        }
      } else {
        this.finalizeDeletion(index);
      }
    }
  }

  private finalizeDeletion(index: number) {
    this.attachments.splice(index, 1);
    this.attachments = [...this.attachments]; // Trigger table refresh
    this.syncSourceAttachments();
  }

  public onClickAddNewEvidence(attachment_type: string) {
    const dialogRef = this.dialog.open(ModalAddAttachmentComponent, {
      panelClass: 'large-dialog',
      data: {
        title: 'modals.modal-add-attachment.add-evidences',
        attachmentType: attachment_type,
        uploadUrl: () => {
          let url = 'upload/tmp-expert-proposal-file';
          if (this.surveyId && attachment_type === 'FILE') {
            if (this.questionId) {
              url = `surveys/${this.surveyId}/questions/${this.questionId}/attachments`;
            } else {
              url = `surveys/${this.surveyId}/attachments`;
            }
          }
          return url;
        },
      },
    });

    dialogRef.afterClosed().subscribe((attachmentData) => {
      if (attachmentData) {
        let newAttachments = [];
        if (["PUBMED", "FILE", "LINK"].includes(attachmentData.attachmentType)) {
          if (attachmentData.attachmentType === "PUBMED") {
            const articles = Array.isArray(attachmentData.attachmentObject)
              ? attachmentData.attachmentObject
              : [attachmentData.attachmentObject];

            articles.forEach((art) => {
              const obj = {
                type: 'PUBMED',
                PMID: art.PMID,
                title: art.ArticleTitle || art.title,
                link: art.link,
                created_at: new Date().toISOString(),
                isNew: true,
              };

              // Pubmed always needs a second call to persist if not in draft
              if (this.surveyId) {
                const body = { PMID: obj.PMID, title: obj.title, link: obj.link };
                if (this.questionId) {
                  this.surveyService.addQuestionAttachment(this.surveyId, this.questionId, 'PUBMED', body, (res) => {
                    this.attachments.push(res);
                    this.attachments = [...this.attachments];
                this.syncSourceAttachments();
                  });
                } else {
                  this.surveyService.addSurveyAttachment(this.surveyId, 'PUBMED', body, (res) => {
                    this.attachments.push(res);
                    this.attachments = [...this.attachments];
                this.syncSourceAttachments();
                  });
                }
              } else {
                this.attachments.push(obj);
                this.attachments = [...this.attachments];
                this.syncSourceAttachments();
              }
            });
          } else if (attachmentData.attachmentType === "LINK") {
            const obj = {
              ...attachmentData.attachmentObject,
              type: 'LINK',
              created_at: new Date().toISOString(),
              isNew: true,
            };

            // Link needs a second call to persist if not in draft
            if (this.surveyId) {
              const body = { title: obj.title, link: obj.link };
              if (this.questionId) {
                this.surveyService.addQuestionAttachment(this.surveyId, this.questionId, 'LINK', body, (res) => {
                  this.attachments.push(res);
                  this.attachments = [...this.attachments];
                this.syncSourceAttachments();
                });
              } else {
                this.surveyService.addSurveyAttachment(this.surveyId, 'LINK', body, (res) => {
                  this.attachments.push(res);
                  this.attachments = [...this.attachments];
                this.syncSourceAttachments();
                });
              }
            } else {
              this.attachments.push(obj);
              this.attachments = [...this.attachments];
                this.syncSourceAttachments();
            }
          } else {
            // Processing FILE
            if (this.surveyId) {
              // Direct upload already returned the saved objects (or array of objects)
              // Handle both single and array formats
              const savedObjects = Array.isArray(attachmentData.attachmentObject)
                ? attachmentData.attachmentObject
                : [attachmentData.attachmentObject];

              this.attachments.push(...savedObjects);
              this.attachments = [...this.attachments];
                this.syncSourceAttachments();
            } else {
              // Draft mode - uses tmp files
              attachmentData.attachmentObject?.forEach((file) => {
                const obj = {
                  ...file,
                  type: 'FILE',
                  created_at: new Date().toISOString(),
                  isNew: true,
                };
                this.attachments.push(obj);
              });
              this.attachments = [...this.attachments];
                this.syncSourceAttachments();
            }
          }
        }
      }
    });
  }
}
